import React from "react";

import { useGetFarms, useTranslation } from "utils";

import { CROP_TYPE_FCPO } from "models";

import {
  Card,
  Checkbox,
  CounterBadge,
  CropRotationCardProps,
  CropRotationRecommendationCard,
  Flex,
  HideFCPOFeatureForUnenrolledFarms,
  Icon,
  PracticeBundleOpts,
  TagNormal,
  TooltipWrapper,
  Typography,
  YearlyEmptyCropCard,
} from "components";

import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";

export type FieldManagementCropCardProps = Partial<CropRotationCardProps> & {
  activePracticeBundles: PracticeBundleOpts[];
  cropMixId?: string;
  fieldId: string;
  isBaseline?: boolean;
  isConfirmed?: boolean;
  isEmptyWithNoEnrollment?: boolean;
  isFetching: boolean;
  isFlaggedByVerifier?: boolean;
  isNarrow: boolean;
  isReportingYear?: boolean;
  isSelected: boolean;
  nameContainerRef: React.RefObject<HTMLParagraphElement>;
  nameRef: React.RefObject<HTMLParagraphElement>;
  shouldHideBaselineTag: boolean;
  shouldHideIcons: boolean;
  shouldShowNameTooltip: boolean;
  // Multi-selection props
  isChecked?: boolean;
  isSelectable?: boolean;
  openTasksCount?: number;
  year?: number;
};

export const CompletenessDataBadge = ({ isComplete, isConfirmed, openTaskCount }: { isComplete: boolean; isConfirmed: boolean; openTaskCount?: number }) => (
  <Flex align="center" justify="center" data-testid={`completeness-data-badge-${isComplete ? "complete" : "incomplete"}`}>
    {isConfirmed && !openTaskCount ? (
      <Icon iconName={"tabler/lock"} className={"icon inline lock-icon"} size="s" />
    ) : isComplete ? (
      <Icon iconName={"tabler/check"} className={"icon inline checkmark-icon"} size="s" />
    ) : (
      <CounterBadge count={openTaskCount ?? 0} />
    )}
  </Flex>
);

export const FieldManagementCropCard = ({
  activePracticeBundles = [],
  baselineYearRange,
  cropDetailsLines,
  cropId,
  cropMixId,
  cropName,
  cropType,
  dataTestId,
  enrolledInInventoryProgram,
  fieldId,
  isBaseline,
  isChecked = false,
  isConfirmed,
  isEmptyCard,
  isEmptyWithNoEnrollment = false,
  isFetching,
  isFlaggedByVerifier = false,
  isInactive = false,
  isMandatory,
  isNarrow,
  isReportingYear,
  isSelected,
  nameContainerRef,
  nameRef,
  openTasksCount,
  recommendations,
  shouldHideBaselineTag,
  shouldHideIcons,
  shouldShowNameTooltip,
  year,
  onClick,
}: FieldManagementCropCardProps) => {
  const { t } = useTranslation(["fieldTimeline", "allFieldsDataTableView"]);

  const { currentFarm } = useGetFarms();

  const { isSubmissionFeatureEnabled } = useSubmissionStatus();

  const getIconBySlug = (slug: string) =>
    ({
      fertilizer: "klim/fertiliser",
      tillage: "klim/tillage",
    })[slug] ?? "";

  if (isEmptyCard) {
    return (
      <YearlyEmptyCropCard
        cropType={cropType!}
        dataTestId={dataTestId}
        fieldId={fieldId}
        handleClick={isFetching ? () => {} : onClick}
        isChecked={isChecked}
        isEmptyWithNoEnrollment={isEmptyWithNoEnrollment}
        isFetching={isFetching}
        isFlaggedByVerifier={isFlaggedByVerifier}
        isInactive={isInactive}
        isMandatory={isMandatory ?? false}
        isNarrow={isNarrow}
        isSelected={isSelected}
        openTasksCount={openTasksCount}
        year={year}
      />
    );
  }

  if (recommendations) {
    return (
      <CropRotationRecommendationCard
        className="yearly-view"
        cropType={cropType!}
        currency={currentFarm?.carbonProgram?.currency}
        dataTestId={dataTestId}
        isInactive={isInactive}
        isSelected={isSelected}
        recommendations={recommendations}
        year={year}
      />
    );
  }

  const NewSubmission = () =>
    !shouldHideIcons && (
      <CompletenessDataBadge isComplete={openTasksCount === 0 || !openTasksCount} isConfirmed={!!isConfirmed} openTaskCount={openTasksCount} />
    );

  const OldSubmission = () => (
    <HideFCPOFeatureForUnenrolledFarms>
      <>
        {!shouldHideIcons && (
          <>
            {cropType !== CROP_TYPE_FCPO.UNDERSOWING
              ? activePracticeBundles?.map((bundle) => (
                  <TooltipWrapper
                    key={bundle.slug}
                    tooltip={{ placement: "top", title: t(`cropCard.practiceBundles.${bundle.slug}`) }}
                    className={`practice-bundle-icon ${bundle.hasMissingData ? "incomplete" : ""}`}
                  >
                    <Icon iconName={getIconBySlug(bundle.slug)} className="icon inline" dataTestId={`icon-${bundle.slug}`} />
                  </TooltipWrapper>
                ))
              : null}
          </>
        )}
        {!shouldHideIcons && enrolledInInventoryProgram && isReportingYear && (
          <CompletenessDataBadge isComplete={openTasksCount === 0 || !openTasksCount} isConfirmed={!!isConfirmed} openTaskCount={openTasksCount} />
        )}
      </>
    </HideFCPOFeatureForUnenrolledFarms>
  );

  return (
    <Card
      className={`crop-rotation-card layout  ${isSelected ? "selected" : isInactive ? "inactive-card" : ""} ${isNarrow ? "is-narrow" : ""} ${
        !cropId ? "unknown" : ""
      }`}
      data-fieldid={fieldId}
      data-testid={`crop-rotation-card-${dataTestId}`}
      tabIndex={0}
      onClick={isFetching ? () => {} : onClick}
    >
      <Flex align="center" justify="center" gap="0.5rem">
        {/* Handling onClick in AllFieldsTimelines (handleCardClick()) to also check the box when the crop card (not just the checkbox) is clicked. */}
        {!isInactive && <Checkbox checked={isChecked} data-testid={`crop-checkbox-${dataTestId}`} />}
        <Flex
          ref={nameContainerRef}
          data-testid={`crop-rotation-card-${dataTestId}-header-name`}
          className={`name-container ${isNarrow ? "is-narrow" : ""}`}
          align="center"
          gap=".125rem"
        >
          <TooltipWrapper
            variant="compact"
            tooltip={
              shouldShowNameTooltip || isNarrow || shouldHideIcons
                ? {
                    placement: "topLeft",
                    title: <>{(shouldShowNameTooltip || isNarrow) && cropName}</>,
                  }
                : {}
            }
          >
            <Flex align="center" gap="0.25rem">
              {cropMixId && <Icon className="cmc-plant-icon" iconName="klim/plant-ai" />}
              <span ref={nameRef} className="name">
                {isNarrow ? "..." : cropName}
              </span>
            </Flex>
          </TooltipWrapper>
        </Flex>
        {!shouldHideIcons && cropDetailsLines && cropDetailsLines.length > 0 && (
          <TooltipWrapper
            variant="neutral"
            className="crop-details-eye-icon-tooltip"
            tooltip={{
              placement: "top",
              title: (
                <Flex vertical gap="0.5rem">
                  {cropDetailsLines.map(({ key, label, entries }) => (
                    <Flex vertical key={key}>
                      <Typography variant="paragraph-xs" color="grey-600" className="margin-0">
                        {label}
                      </Typography>
                      <Typography variant="paragraph-s" className="margin-0">
                        {entries.join(", ")}
                      </Typography>
                    </Flex>
                  ))}
                </Flex>
              ),
            }}
          >
            <Icon
              iconName="tabler/eye"
              className="icon inline crop-details-eye-icon"
              size="s"
              dataTestId={`crop-details-eye-icon-${dataTestId}`}
              onClick={(e) => e.stopPropagation()}
            />
          </TooltipWrapper>
        )}
        {isBaseline && !shouldHideBaselineTag && (
          <TooltipWrapper
            variant="neutral"
            className="baseline-tag-tooltip"
            tooltip={{ title: baselineYearRange ? t("allFieldsDataTableView:table.data.baselineTooltip", baselineYearRange) : t("cropCard.baseline") }}
          >
            <TagNormal className="baseline-tag" text={t("cropCard.baseline")} />
          </TooltipWrapper>
        )}
        {isSubmissionFeatureEnabled ? <NewSubmission /> : <OldSubmission />}
      </Flex>
    </Card>
  );
};
